import { LocalDate } from "@js-joda/core";
import { AxiosError } from "axios";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { MonthWorkingHours } from "model/Salary";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { getMonthNameFromPeriod } from "web-apps/company/utils/utils";
import { IconButton } from "web-apps/company/components/timeReportsV2/components/views/person/components/row/personSummaryRow.styled";
import {
  Lables,
  MonthList,
  MonthRow,
  MonthTitle,
  YearNavWrapper,
} from "./editWorkingHoursModal.styled";

type Props = {
  setEditWorkingHours: React.Dispatch<React.SetStateAction<boolean>>;
  editWorkingHours: boolean;
};

export const EditWorkingHoursModal: React.FC<Props> = ({
  setEditWorkingHours,
  editWorkingHours,
}) => {
  const { t } = useTranslation();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [monthData, setMonthData] = useState<MonthWorkingHours[]>([]);
  const [{ company }] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [selectedMonth, setSelectedMonth] = useState<string>();

  const api = useMemo(() => Api(), []);

  const companyId = company?.id;

  const handleModalClose = useCallback(() => {
    setEditWorkingHours(false);
  }, [setEditWorkingHours]);

  const handleHoursChange = useCallback((period: string, value: string) => {
    const newValue = value === "" ? 0 : parseInt(value);

    setMonthData((prevMonthData) =>
      prevMonthData.map((month) =>
        month.period === period ? { ...month, hours: newValue } : month,
      ),
    );
  }, []);

  const overrideMonthWorkingHours = useCallback(
    (period: string) => {
      if (!companyId) return;

      const monthHours = monthData.find((m) => m.period === period)?.hours || 0;

      api.company.salary
        .overrideMonthWorkingHours(companyId, period, monthHours)
        .then(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "checkmark",
              title: t("Salary.Changed"),
              message: t("Salary.ChangedWorkingHours"),
            },
          });
        })
        .catch(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "alert",
              title: t("Alerts.OhNo"),
              message: t("Alerts.SomethingWentWrong"),
            },
          });
        });
    },
    [api, companyId, monthData, alertDispatch, t],
  );

  useEffect(() => {
    if (!companyId) return;

    let isMounted = true;

    api.company.salary
      .getCompanyWorkHours(companyId, currentYear)
      .then((res) => {
        if (isMounted) {
          setMonthData(res.data);
        }
      })
      .catch((e: AxiosError) => {
        console.error("Error when getting work hours", e);
      });

    return () => {
      isMounted = false;
    };
  }, [currentYear, companyId, api]);

  const thisYear = new Date().getFullYear();
  const allowPrev = currentYear > thisYear;
  const allowNext = currentYear < thisYear + 2;

  return (
    <Modal
      isOpen={editWorkingHours}
      onClose={handleModalClose}
      label={t("Salary.MonthHours")}
      size="small"
    >
      <YearNavWrapper>
        <PeriodNavigator
          currentDate={LocalDate.of(currentYear, 1, 1)}
          mode="year"
          onChange={(date) => setCurrentYear(date.year())}
          allowPrev={allowPrev}
          allowNext={allowNext}
        />
      </YearNavWrapper>
      <MonthList>
        <MonthRow>
          <Lables>{t("Salary.Month")}</Lables>
          <Lables>{t("Salary.Hours")}</Lables>
        </MonthRow>
        {monthData.map((month) => (
          <MonthRow key={month.period}>
            <MonthTitle>{getMonthNameFromPeriod(month.period)}</MonthTitle>
            <InputField
              type="number"
              value={month.hours.toString()}
              onChange={(value) => handleHoursChange(month.period, value)}
              onFocus={() => setSelectedMonth(month.period)}
              placeholder="0"
              maxLength={3}
              size="compact"
            />
            {selectedMonth === month.period ? (
              <IconButton
                onClick={() => {
                  overrideMonthWorkingHours(month.period);
                  setSelectedMonth(undefined);
                }}
              >
                <CustomIcon
                  name="v2/checkmark"
                  size="20px"
                  color={ColorV2.SimflexityGreen}
                />
              </IconButton>
            ) : (
              <span />
            )}
          </MonthRow>
        ))}
      </MonthList>
    </Modal>
  );
};
